// Libraries
import { useEffect, useState } from 'react';

// Hooks
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import useSound from '../../../hooks/useSound';

// Features
import { selectUser } from '../../../features/user/userSlice';
import {
    selectSession,
    selectSessionStatus,
    selectCurrentGame,
    selectPlayerList,
    pushManualDaub,
    postBingoClaimAsync,
} from '../../../features/session/sessionSlice';

// Components
import StateScreen from '../../templates/StateScreen/StateScreen';
import SessionScreen from '../../organisms/SessionScreen/SessionScreen';

import CardGrid from '../../molecules/CardGrid/CardGrid';
import FlashBoard from '../../molecules/FlashBoard/FlashBoard';
import Patterns from '../../molecules/Patterns/Patterns';
import GameStats from '../../molecules/GameStats/GameStats';
import SessionStats from '../../molecules/SessionStats/SessionStats';
import ToGoList from '../../molecules/ToGoList/ToGoList';

import BingoButton from '../../atoms/BingoButton/BingoButton';
import CallNumber from '../../atoms/CallNumber/CallNumber';
import Card from '../../atoms/Card/Card';
import CurrentBall from '../../atoms/CurrentBall/CurrentBall';

// Utilities
import sortByObjectProp from '../../../utils/object/sortByObjectProp';
import getRemainingTotal from '../../../utils/misc/getRemainingTotal';
import getCurrentPotTotal from '../../../utils/misc/getCurrentPotTotal';
import { pushSplashMessage } from '../../../features/app/appSlice';
import playSound from '../../../utils/misc/playSound';

// Constants
const maxClaimAttempts = 5;

/**
 * @function Game
 * Active game screen
 *
 * @example
 * return (
 *   <Game />
 * )
 */
function Game(): JSX.Element {
    const [invalidBingoClaims, setInvalidBingoClaims] = useState<number>(0);
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const session = useAppSelector(selectSession);
    const sessionStatus = useAppSelector(selectSessionStatus);
    const game = useAppSelector(selectCurrentGame);
    const players = useAppSelector(selectPlayerList);

    const voice = user.voice !== '' ? user.voice : 'default';
    const daubSoundUrl = `https://${process.env.REACT_APP_CDN}/play-bingo/audio/default/daub.mp3`;
    const heartbeatSoundUrl = `https://${process.env.REACT_APP_CDN}/play-bingo/audio/default/heartbeat.mp3`;
    const noBingoSoundUrl = `https://${process.env.REACT_APP_CDN}/play-bingo/audio/${voice}/nobingo.mp3`;

    if (session === null || session === undefined) return <></>;
    const gameMeta = session.games.find(({ id }) => id === game.id);
    if (gameMeta === undefined) return <></>;

    const columns = gameMeta.game.category?.grid_col_count;
    const rows = gameMeta.game.category?.grid_row_count;
    const patterns = gameMeta.game.gamePatterns;
    const activePatterns = patterns.filter(
        ({ pattern }) => !game.patternsWon.includes(pattern.id),
    );

    const perRow = 4;
    const prizePoints = `${getCurrentPotTotal(game.currentPot)} PP`;
    const wildcards = gameMeta.wildcards;
    const baseCards = gameMeta.cards;

    const cards = baseCards.map((card, index) => {
        const manualDaubCard = game.manualDaubList.find(
            ({ cardId }) => cardId === index,
        );
        const manualDaubCardNumbers =
            manualDaubCard !== undefined ? manualDaubCard.numbers : [];
        const [remaining, validNumbers] = getRemainingTotal(
            game.called,
            activePatterns,
            card,
            wildcards[index],
            session.session.is_manual_daubing ? manualDaubCardNumbers : false,
        );
        return {
            id: index,
            numbers: card,
            wildcards: wildcards[index],
            remaining,
            validNumbers,
            manual: session.session.is_manual_daubing,
        };
    });

    const orderedCards = !session.session.is_manual_daubing
        ? sortByObjectProp([...cards], 'remaining')
        : cards;

    const gamePlayable = cards.length > 0 ? true : false;
    const oneToGoCards = cards.filter(({ remaining }) => remaining <= 1);
    const winningCards = cards.filter(({ remaining }) => remaining === 0);
    const noBingoSound = useSound(noBingoSoundUrl);
    const heartbeatSound = useSound(heartbeatSoundUrl);

    // Inform player that they are close to matching a pattern
    useEffect(() => {
        if (
            gamePlayable === true &&
            oneToGoCards.length > 0 &&
            session.session.is_manual_daubing === false
        ) {
            if (user.heartbeat_on === true) playSound(heartbeatSound);
        }
    }, [winningCards]);

    // Handle a manual daub click
    function handleDaubClick(id: number, number: number) {
        const daubSound = new Audio(daubSoundUrl);
        if (user.sound_on === true) playSound(daubSound);

        dispatch(
            pushManualDaub({
                id: id,
                number: number,
            }),
        );
    }

    // Determine whether the player has correctly claimed bingo (manual daubing)
    function handleClaimClick() {
        if (
            invalidBingoClaims < 5 &&
            winningCards.length > 0 &&
            game.manualClaimState === 'pending'
        ) {
            dispatch(
                postBingoClaimAsync({
                    gameId: game.id,
                    cards: winningCards.map(({ id }) => id),
                    patternId: 0,
                }),
            );
        } else if (invalidBingoClaims < 5 && winningCards.length <= 0) {
            const newClaimCount = invalidBingoClaims + 1;
            setInvalidBingoClaims(newClaimCount);
            if (user.sound_on === true) playSound(noBingoSound);
            dispatch(
                pushSplashMessage(
                    `You do not have bingo. You have ${
                        maxClaimAttempts - newClaimCount
                    } attempts remaining.`,
                ),
            );
        } else if (game.manualClaimState !== 'pending') {
            dispatch(pushSplashMessage(`You have already claimed bingo.`));
        } else {
            dispatch(
                pushSplashMessage(
                    `You have incorrectly claimed bingo too many times and can no longer claim bingo this game.`,
                ),
            );
            setInvalidBingoClaims(
                (invalidBingoClaims) => invalidBingoClaims + 1,
            );
        }
    }

    // Display additional game data based on the type of game
    function renderAdditionalComponents(isManual: boolean) {
        if (
            isManual &&
            sessionStatus === 'loading' &&
            game.manualClaimState === 'pending'
        ) {
            return <BingoButton text="Validating claim..." disabled={true} />;
        } else if (isManual && game.manualClaimState === 'successful') {
            return (
                <BingoButton
                    text="Waiting for other players..."
                    disabled={true}
                />
            );
        } else if (isManual && game.manualClaimState === 'failed') {
            return (
                <BingoButton
                    text="Sorry, you have claimed too late..."
                    disabled={true}
                />
            );
        } else if (isManual) {
            return (
                <BingoButton
                    text="Claim Bingo"
                    onClick={() => handleClaimClick()}
                />
            );
        } else {
            return <ToGoList cards={orderedCards} />;
        }
    }

    return (
        <SessionScreen>
            <SessionStats
                gameCount={gameMeta.sort_order}
                gameTotal={session.games.length}
                prizePoints={prizePoints}
                cardCount={cards.length}
                playerCount={players.length}
            />
            <GameStats>
                <CallNumber
                    number={game.call}
                    visible={!session.session.is_live_stream}
                />

                <CurrentBall
                    number={game.ball}
                    animation={true}
                    length={
                        session.session.is_manual_daubing ? 'long' : 'short'
                    }
                />
                <Patterns
                    patterns={activePatterns}
                    rows={rows}
                    columns={columns}
                />
                {user.flashcard_on ? (
                    <FlashBoard called={game.called} />
                ) : (
                    <></>
                )}
            </GameStats>
            {gamePlayable === true ? (
                <>
                    {renderAdditionalComponents(
                        session.session.is_manual_daubing,
                    )}
                    <CardGrid perRow={perRow}>
                        {orderedCards.map((item, index) => {
                            const manualDaubCard = game.manualDaubList.find(
                                ({ cardId }) => cardId === index,
                            );
                            return (
                                <Card
                                    key={item.id}
                                    id={item.id}
                                    numbers={item.numbers}
                                    wildcards={item.wildcards}
                                    validNumbers={item.validNumbers}
                                    daubedNumbers={
                                        manualDaubCard !== undefined
                                            ? manualDaubCard.numbers
                                            : []
                                    }
                                    remaining={item.remaining}
                                    manual={session.session.is_manual_daubing}
                                    rows={rows}
                                    columns={columns}
                                    patterns={activePatterns}
                                    onClick={(id: number, item: number) =>
                                        handleDaubClick(id, item)
                                    }
                                />
                            );
                        })}
                    </CardGrid>
                </>
            ) : (
                <StateScreen title="Waiting for the current game to finish">
                    {session?.games.length > 1 ? (
                        <p>
                            Your cards will be generated at the start of the
                            next game.
                        </p>
                    ) : (
                        <p>
                            You can wait here to purchase cards for the next
                            game or you can join the waiting room from the
                            lobby.
                        </p>
                    )}
                </StateScreen>
            )}
        </SessionScreen>
    );
}

// Exports
export default Game;
